import type { AccrualVarTableType } from '@/types/EMRALD_Model';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ChangeEvent, useEffect, useState } from 'react';
import {
  StyledTableCell,
  StyledTableRow,
} from '@/components/forms/ActionForm/ActionToStateTable';
import { useVariableFormContext } from '../VariableFormContext';

export const StateTable: React.FC = () => {
  const { setValue, accrualStatesData, setAccrualStatesData, sync }
    = useVariableFormContext();
  const [accrualMults, setAccrualMults] = useState<number[]>([]);
  const [multRates, setMultRates] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [accrualTables, setAccrualTables] = useState<number[][][]>([]);
  const [tableMinimized, setTableMinimized] = useState<boolean[]>([]);

  useEffect(() => {
    if (accrualStatesData && accrualStatesData.length > 0) {
      setAccrualMults(accrualStatesData.map(state => state.accrualMult));
      setMultRates(accrualStatesData.map(state => state.multRate || ''));
      setTypes(accrualStatesData.map(state => state.type));
      setAccrualTables(accrualStatesData.map(state => state.accrualTable));
    }
  }, [
    accrualStatesData,
    JSON.stringify(multRates),
    JSON.stringify(types),
    JSON.stringify(accrualTables),
  ]);

  useEffect(() => {
    const newAccrualTables = [...accrualTables];
    // Check if there are more accrual states than arrays in accrualTables
    if (
      accrualStatesData
      && accrualStatesData.length > newAccrualTables.length
    ) {
      // Add empty arrays for each new accrual state
      for (let i = newAccrualTables.length; i < accrualStatesData.length; i++) {
        newAccrualTables.push([]);
      }
      setAccrualTables(newAccrualTables);
    }
    console.log('Syncing accrual states');
    sync({ accrualStatesData });
  }, [accrualStatesData, JSON.stringify(accrualTables)]);

  useEffect(() => {
    setValue(0);
  }, []);

  const handleStaticAccrualMultChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const parsedValue = Number.parseFloat(e.target.value); // Convert string to number
    if (!Number.isNaN(parsedValue)) {
      if (accrualStatesData && accrualStatesData[index]) {
        accrualStatesData[index].accrualMult = parsedValue;
      }
      const newAccrualMults = [...accrualMults];
      newAccrualMults[index] = parsedValue;
      setAccrualMults(newAccrualMults);
    }
  };
  const handleMultRateChange = (event: SelectChangeEvent, index: number) => {
    if (accrualStatesData && accrualStatesData[index]) {
      accrualStatesData[index].multRate = event.target.value;
    }
    const newMultRates = [...multRates];
    newMultRates[index] = event.target.value;
    setMultRates(newMultRates);
  };
  const handleTypeChange = (event: SelectChangeEvent, index: number) => {
    if (accrualStatesData && accrualStatesData[index]) {
      accrualStatesData[index].type = event.target.value as AccrualVarTableType;
    }
    const newTypes = [...types];
    newTypes[index] = event.target.value as AccrualVarTableType;
    setTypes(newTypes);
  };

  function handleAccrualTableChange(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    idx: number,
    arg2: number,
  ): void {
    const parsedValue = Number.parseFloat(event.target.value); // Convert string to number
    if (!Number.isNaN(parsedValue)) {
      const newAccrualTables = [...accrualTables];
      if (newAccrualTables[index] && newAccrualTables[index][idx]) {
        newAccrualTables[index][idx][arg2] = parsedValue;
      }
      setAccrualTables(newAccrualTables);
      if (accrualStatesData && accrualStatesData[index]) {
        accrualStatesData[index].accrualTable[idx]
          = newAccrualTables[index]?.[idx] ?? [];
      }
    }
  }

  const addRow = (index: number) => {
    const newTables = [...accrualTables];
    newTables[index]?.push([0, 0]);
    setAccrualTables(newTables);
  };
  const deleteAccrualState = (index: number) => {
    if (accrualStatesData) {
      const newAccrualStates = [...accrualStatesData];
      newAccrualStates.splice(index, 1);
      setAccrualStatesData(newAccrualStates);
    }
  };

  function deleteRow(index: number, idx: number): void {
    const newTables = [...accrualTables];
    newTables[index]?.splice(idx, 1);
    setAccrualTables(newTables);
  }

  function addRowBelow(index: number, idx: number): void {
    const newTables = [...accrualTables];
    const newRow = [0, 0]; // Default values for the new row

    // Calculate the index to insert the new row below the specified row
    const insertIndex = idx + 1;

    // Insert the new row into the newTables array
    newTables[index]?.splice(insertIndex, 0, newRow);

    // Update the state with the new tables
    setAccrualTables(newTables);
  }

  function toggleMinimize(index: number): void {
    const newTableMinimized = [...tableMinimized];
    newTableMinimized[index] = !newTableMinimized[index];
    setTableMinimized(newTableMinimized);
  }

  const getMultRateOptions = (index: number, min?: boolean) => {
    return (
      (multRates[index] || multRates[index] === '') && (
        <FormControl
          variant="outlined"
          size="small"
          sx={{
            minWidth: min ? 80 : 120,
            width: min ? 'min-content' : '100%',
            my: 1,
            '& .MuiInputLabel-root': { fontSize: '0.875rem' },
            '& .MuiSelect-select': { fontSize: '0.875rem' },
          }}
        >
          <InputLabel id="rate-label">
            {min ? 'Rate' : 'Multiplication Rate'}
          </InputLabel>
          <Select
            aria-labelledby="rate-label"
            defaultValue={multRates[index]}
            value={multRates[index]}
            onChange={(event: SelectChangeEvent) => {
              handleMultRateChange(event, index);
            }}
            label={min ? 'Rate' : 'Multiplication Rate'}
          >
            <MenuItem value="trSeconds" sx={{ fontSize: '0.875rem' }}>
              {min ? 'Sec' : 'Second'}
            </MenuItem>
            <MenuItem value="trMinutes" sx={{ fontSize: '0.875rem' }}>
              {min ? 'Min' : 'Minute'}
            </MenuItem>
            <MenuItem value="trHours" sx={{ fontSize: '0.875rem' }}>
              {min ? 'Hr' : 'Hour'}
            </MenuItem>
            <MenuItem value="trDays" sx={{ fontSize: '0.875rem' }}>
              {min ? 'Day' : 'Day'}
            </MenuItem>
          </Select>
        </FormControl>
      )
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table aria-label="State Table">
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Name</StyledTableCell>
            <StyledTableCell sx={{ width: '15%' }}>Type</StyledTableCell>
            <StyledTableCell>Accrual Rate</StyledTableCell>
            <StyledTableCell>Command</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {accrualStatesData?.map((item, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell>
                <Typography variant="body2" fontWeight={500}>
                  {item.stateName}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                {types[index] && (
                  <RadioGroup
                    sx={{
                      margin: '8px',
                      '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                    }}
                    aria-label="status-value"
                    name="status-value"
                    value={types[index]}
                    onChange={event => {
                      handleTypeChange(event, index);
                    }}
                    row
                  >
                    <FormControlLabel
                      value="ctMultiplier"
                      control={
                        <Radio
                          size="small"
                          checked={types[index] === 'ctMultiplier'}
                        />
                      }
                      label="Static"
                    />
                    <FormControlLabel
                      value="ctTable"
                      control={
                        <Radio
                          size="small"
                          checked={types[index] === 'ctTable'}
                        />
                      }
                      label="Dynamic"
                    />
                  </RadioGroup>
                )}
              </StyledTableCell>
              {types[index] === 'ctMultiplier' ? (
                <StyledTableCell>
                  <TextField
                    label="Accrual Multiplication Factor"
                    variant="outlined"
                    size="small"
                    multiline
                    margin="normal"
                    type="number"
                    value={accrualMults[index]}
                    onChange={event => {
                      handleStaticAccrualMultChange(event, index);
                    }}
                  />
                  <Typography variant="body2">per</Typography>
                  {getMultRateOptions(index)}
                </StyledTableCell>
              ) : (
                <StyledTableCell>
                  <Button
                    onClick={() => {
                      toggleMinimize(index);
                    }}
                  >
                    {tableMinimized[index] ? 'Expand Table' : 'Hide Table'}
                  </Button>
                  <Button
                    onClick={() => {
                      addRow(index);
                    }}
                  >
                    Add Row
                  </Button>

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableCell>
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2" sx={{ mx: 1 }}>
                                Simulation Time
                              </Typography>
                              &nbsp;
                              {getMultRateOptions(index, true)}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2" sx={{ mx: 1 }}>
                                Accrual Rate
                              </Typography>
                              &nbsp;
                              {getMultRateOptions(index, true)}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2" sx={{ mx: 1 }}>
                              Command
                            </Typography>
                          </StyledTableCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {accrualTables[index]
                          && !tableMinimized[index]
                          && accrualTables[index].map(
                            (item: number[], idx: number) => (
                              <StyledTableRow key={idx}>
                                <StyledTableCell>
                                  <TextField
                                    label="Simulation Time"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    type="number"
                                    value={item[0]}
                                    onChange={(event): void => {
                                      handleAccrualTableChange(
                                        event,
                                        index,
                                        idx,
                                        0,
                                      );
                                    }}
                                    sx={{ width: '90%' }}
                                  />
                                </StyledTableCell>
                                <StyledTableCell sx={{ display: 'flex' }}>
                                  <TextField
                                    label="Accrual Rate"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    type="number"
                                    value={item[1]}
                                    onChange={(event): void => {
                                      handleAccrualTableChange(
                                        event,
                                        index,
                                        idx,
                                        1,
                                      );
                                    }}
                                    sx={{ width: '85%' }}
                                  />
                                </StyledTableCell>
                                <StyledTableCell>
                                  <Tooltip title="Add Row Below">
                                    <AddIcon
                                      sx={{ cursor: 'pointer', ml: 1 }}
                                      onClick={() => {
                                        addRowBelow(index, idx);
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Delete Row">
                                    <DeleteIcon
                                      sx={{ cursor: 'pointer', ml: 3 }}
                                      onClick={() => {
                                        deleteRow(index, idx);
                                      }}
                                    />
                                  </Tooltip>
                                </StyledTableCell>
                              </StyledTableRow>
                            ),
                          )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </StyledTableCell>
              )}

              <StyledTableCell>
                <Tooltip title="Delete State">
                  <DeleteIcon
                    sx={{ cursor: 'pointer', ml: 3 }}
                    onClick={() => {
                      deleteAccrualState(index);
                    }}
                  />
                </Tooltip>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
